/**
 * AIChatWidget - Popup chat cu 3 moduri: Suport, Prețuri & Sesizare Eroare
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Send, Bot, User, Loader2, Minimize2, Sparkles, HelpCircle, Headphones, Calculator, AlertTriangle, Mic, MicOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Răspunsuri predefinite pentru suport platformă
const SUPPORT_RESPONSES = {
  "pontaj": "📅 **Calendar Pontaj**\n\nPentru a gestiona pontajul:\n1. Accesează tab-ul 'Pontaj' din meniu\n2. Click pe orice celulă din calendar pentru a edita\n3. Selectează statusul dorit (Prezent, CO, CM, etc.)\n4. Salvează modificările\n\n💡 Tip: Poți exporta pontajul în PDF sau Excel!",
  "angajat": "👥 **Adăugare Angajați**\n\n1. Mergi la tab-ul 'Angajați'\n2. Click pe butonul verde '+ Adaugă Angajat'\n3. Completează datele (nume, email, telefon)\n4. Angajatul primește automat acces la aplicație\n\n💡 Parola inițială este trimisă pe email.\n💰 Cost: 20 credite la adăugare + 20 credite/lună per angajat activ.",
  "santier": "📍 **Gestionare Șantiere**\n\n1. Accesează 'Șantiere' din meniu\n2. Adaugă un șantier nou cu adresa și coordonate GPS\n3. Angajații vor selecta șantierul la check-in\n\n💡 Coordonatele GPS permit validarea locației.",
  "unelte": "🔧 **Inventar Unelte**\n\n1. Tab-ul 'Unelte' afișează toate echipamentele\n2. Poți adăuga unelte noi și urmări utilizarea\n3. Sistemul previne rezervarea dublă\n\n💡 Vezi disponibilitatea în timp real!",
  "foaie parcurs": "🚗 **Foi de Parcurs**\n\n1. Angajații completează km la plecare/sosire\n2. Administratorul poate închide foi incomplete\n3. Toate foile se arhivează automat în Documente\n\n💡 Poți genera rapoarte lunare.\n💰 Cost: 10 credite per export Excel.",
  "concediu": "🏖️ **Cereri Concediu**\n\nAngajații pot cere concediu din aplicația mobilă:\n- CO (Concediu Odihnă)\n- CM (Concediu Medical)\n- CS (Concediu Special)\n\nAdministratorul aprobă/respinge din Notificări.",
  "notificari": "🔔 **Centru Notificări**\n\nPrimești alerte pentru:\n- Cereri de concediu\n- Solicitări pauză masă\n- Angajați cu pontaj incomplet\n\n💡 Poți șterge notificările în bloc.",
  "export": "📄 **Export Date**\n\n- **PDF**: Calendar pontaj lunar (numele PDF-ului include beneficiarul devizului)\n- **Excel**: Date detaliate cu multiple foi\n\nAccesează din tab-ul Pontaj butoanele PDF/Excel.",
  "tutorial": "🎓 **Tutorial**\n\nPoți reporni tutorialul oricând:\n1. Click pe iconița 🎓 din header\n2. Parcurge pașii pentru fiecare funcționalitate",
  "parola": "🔐 **Schimbare Parolă**\n\n1. Click pe iconița ⚙️ (Setări) din header\n2. Secțiunea 'Schimbă Parola'\n3. Introdu parola veche și cea nouă\n4. Salvează",
  "deviz": "🧮 **Flux Generare Deviz** (modul DevizPRO)\n\n1. Tab **Deviz** → buton **+ Deviz nou**\n2. Completează antetul: număr deviz, dată, **Beneficiar** (caută după CUI sau introdu manual), obiectiv, adresă\n3. Adaugă **capitole** (ex: Demolări, Zugrăveli) și **articole** (cu cantitate, preț manoperă, preț material)\n4. Folosește **DevizPRO AI** (buton sclipici) ca să generezi articole automat din descriere text\n5. **Salvează** devizul (auto-save activ după 3 secunde)\n6. Click **Generează PDF** — fișierul descărcat va avea formatul `Deviz_{numar}_{data}_{Beneficiar}.pdf`\n\n💰 Cost: 20 credite generare deviz nou, 3 credite editare, 20 credite schimbare beneficiar.\n\n💡 Pe baza devizului poți crea ulterior factură, contract, anexe și procese verbale (vezi 'contract', 'anexa', 'proces verbal').",
  "contract": "📜 **Flux Creare Contract**\n\n1. Tab **Contracte** → **+ Contract nou**\n2. **Selectează șablonul** (Antrepriză, Subantrepriză, Servicii, Custom WYSIWYG)\n3. **Asociază un deviz** existent — datele beneficiarului, valoarea totală și articolele se preiau automat\n4. Completează **datele specifice contractului**: termen, modalitate plată, garanții, clauze speciale\n5. Editorul WYSIWYG permite formatare bogată (titluri, liste, semnături)\n6. **Previzualizează A4** apoi **Generează PDF** sau **Trimite pe email**\n\n💰 Cost: 10 credite per contract generat.\n\n💡 Contractele se arhivează automat în Documente cu legătură către devizul-sursă.",
  "proiect": "🏗️ **Flux Creare Proiect**\n\n1. Tab **Proiecte** → **+ Proiect nou**\n2. Definește: nume proiect, **beneficiar**, **șantier asociat**, perioada start-final\n3. Adaugă **Faze** (ex: Fundație, Structură, Finisaje) cu termene\n4. Atașează **devize**, **contracte**, **echipe** și **utilaje** la fiecare fază\n5. Urmărește progresul în **Pipeline Timeline**: status fiecare fază (planificat / în curs / finalizat)\n6. Generează **Raport Proiect** (buton din pagina proiectului) cu evoluție și costuri reale vs estimate\n\n💡 Proiectul leagă pontajul angajaților cu devizul, ca să vezi profitabilitatea reală.",
  "anexa": "📎 **Flux Creare Anexe**\n\nAnexele sunt documente atașate unui contract sau deviz (ex: situații suplimentare, modificări de cantități).\n\n1. Deschide contractul/devizul-părinte\n2. Click **+ Anexă** (în secțiunea Documente atașate)\n3. Alege tipul anexei: **Suplimentar deviz**, **Modificare contract**, **Acord adițional**\n4. Numerotare automată (ex: Anexa 1 la Contract X / data)\n5. Adaugă articole noi sau modifică cantitățile existente\n6. **Generează PDF** — anexa moștenește antetul și beneficiarul documentului-părinte\n\n💡 Toate anexele apar în Documente, grupate sub documentul-părinte.",
  "proces verbal": "📋 **Flux Procese Verbale (PV)**\n\nPV-urile (recepție lucrări, predare-primire, constatare) se creează din proiect sau direct.\n\n1. Tab **Documente** → **+ Proces verbal** (sau din pagina proiectului)\n2. Alege tipul: **PV Recepție Lucrări**, **PV Predare-Primire Șantier**, **PV Constatare**\n3. Completează: părți semnatare (beneficiar + executant), obiectul recepției, lista de lucrări verificate, observații, calificative\n4. Atașează **fotografii de pe șantier** (drag&drop)\n5. **Semnături**: poți adăuga semnături olografe scanate sau folosi semnătura digitală a clientului din portal\n6. **Generează PDF** și trimite automat pe email beneficiarului\n\n💡 PV-urile se atașează automat la proiect și pot fi referite în contracte/anexe ulterioare.",
  "credite": "💰 **Sistem Credite (1 credit = 1 RON)**\n\nLa **deschiderea fiecărei firme** primești automat **200 credite bonus** de bun-venit. Apoi plătești pe acțiune:\n\n• Adăugare angajat: **20 credite**\n• Taxă lunară angajat activ: **20 credite/lună**\n• Generare deviz: **20 credite** (editare: 3 credite)\n• Schimbare beneficiar deviz: **20 credite**\n• Generare factură: **20 credite** (editare: 3 credite)\n• Generare contract: **10 credite**\n• Foi de parcurs (Excel): **10 credite**\n• Pontaj lunar (PDF/Excel): **10 credite/lună** (descărcări nelimitate după plată)\n• Stat de plată (Excel): **5 credite/lună**\n\n💡 Verifici soldul în header (badge) sau în tab-ul **Plăți → Istoric tranzacții**. SuperAdminul poate atribui credite oricând.",
  "factura": "🧾 **Flux Generare Factură**\n\n1. Tab **Facturi** → **+ Factură nouă**\n2. Selectează **beneficiarul** (sau caută după CUI prin ANAF)\n3. **Importă dintr-un deviz** (recomandat) sau adaugă manual articole\n4. Completează: serie, număr, scadență, modalitate plată, observații\n5. **Generează PDF** sau **Trimite pe email** direct\n6. Marchează ca **plătită** când banii intră în cont (apare în statistici)\n\n💰 Cost: 20 credite generare factură (3 credite editare).\n\n💡 Sincronizare automată cu **e-Factura ANAF** (XML format).",
  "ai": "🤖 **DevizPRO AI**\n\nAsistentul AI generează articole de deviz automat:\n\n1. În deviz, click butonul **✨ DevizPRO**\n2. Descrie lucrarea în text liber (ex: 'zugrăveli 50mp în 2 straturi')\n3. AI-ul propune articole cu cantități, prețuri manoperă și material orientative\n4. Acceptă tot, parțial sau modifică prețurile\n\n💡 AI-ul învață din devizele firmei tale (Deviz Knowledge Base) — cu cât folosești mai mult, cu atât prețurile sunt mai exacte.",
  "login": "🔑 **Probleme Login**\n\n- Verifică email-ul (cu litere mici)\n- Parola este case-sensitive\n- Click 'Am uitat parola' pentru reset prin email\n- Contul nou e activ imediat (fără verificare email)\n\nDacă problema persistă, trimite-ne detalii prin tab-ul **Eroare**!",
  "aplicatie": "📱 **Aplicația Mobilă**\n\nAplicația funcționează ca PWA:\n1. Deschide site-ul pe telefon\n2. Click 'Adaugă pe ecranul principal'\n3. Folosește ca o aplicație normală\n\n💡 Funcționează și offline parțial! Pinch-to-zoom activ pe mobil."
};

// Funcție pentru a găsi răspunsul potrivit
const findSupportResponse = (question) => {
  const q = question.toLowerCase();
  
  for (const [key, response] of Object.entries(SUPPORT_RESPONSES)) {
    if (q.includes(key)) {
      return response;
    }
  }
  
  // Căutare alternativă cu cuvinte cheie
  if (q.includes("calendar") || q.includes("prezent") || q.includes("absent")) return SUPPORT_RESPONSES["pontaj"];
  if (q.includes("muncitor") || q.includes("echipa") || q.includes("adaug")) return SUPPORT_RESPONSES["angajat"];
  if (q.includes("locatie") || q.includes("gps")) return SUPPORT_RESPONSES["santier"];
  if (q.includes("echipament") || q.includes("inventar")) return SUPPORT_RESPONSES["unelte"];
  if (q.includes("masina") || q.includes("km") || q.includes("transport") || q.includes("foi")) return SUPPORT_RESPONSES["foaie parcurs"];
  if (q.includes("liber") || q.includes("medical") || q.includes("vacanta")) return SUPPORT_RESPONSES["concediu"];
  if (q.includes("alerta") || q.includes("mesaj")) return SUPPORT_RESPONSES["notificari"];
  if (q.includes("descarca") || q.includes("raport")) return SUPPORT_RESPONSES["export"];
  if (q.includes("ajutor") || q.includes("invat") || q.includes("cum")) return SUPPORT_RESPONSES["tutorial"];
  if (q.includes("secret") || q.includes("acces") || q.includes("cont")) return SUPPORT_RESPONSES["parola"];
  if (q.includes("pret") || q.includes("cost") || q.includes("calcul") || q.includes("oferta")) return SUPPORT_RESPONSES["deviz"];
  if (q.includes("contract") || q.includes("antrepriza") || q.includes("subantrepriza")) return SUPPORT_RESPONSES["contract"];
  if (q.includes("proiect") || q.includes("faza") || q.includes("pipeline")) return SUPPORT_RESPONSES["proiect"];
  if (q.includes("anex") || q.includes("aditional") || q.includes("supliment")) return SUPPORT_RESPONSES["anexa"];
  if (q.includes("proces verbal") || q.includes(" pv ") || q.startsWith("pv") || q.includes("receptie") || q.includes("predare")) return SUPPORT_RESPONSES["proces verbal"];
  if (q.includes("credit") || q.includes("plata") || q.includes("sold") || q.includes("ron")) return SUPPORT_RESPONSES["credite"];
  if (q.includes("factura") || q.includes("e-factura") || q.includes("anaf")) return SUPPORT_RESPONSES["factura"];
  if (q.includes("ai") || q.includes("inteligenta") || q.includes("automat") || q.includes("genereaza articole")) return SUPPORT_RESPONSES["ai"];
  if (q.includes("intra") || q.includes("conecta") || q.includes("autentific")) return SUPPORT_RESPONSES["login"];
  if (q.includes("telefon") || q.includes("mobil") || q.includes("install")) return SUPPORT_RESPONSES["aplicatie"];
  
  return null;
};

export default function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatMode, setChatMode] = useState("support"); // "support", "prices", "error"
  
  // Get user data from either storage (localStorage cu "Remember me", sessionStorage altfel)
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userData) {
        return JSON.parse(userData);
      }
    } catch (e) {}
    return null;
  };
  
  // Mesaje separate pentru fiecare mod
  const [pricesMessages, setPricesMessages] = useState([
    {
      role: "assistant",
      content: "Bună! 👋 Sunt asistentul tău pentru prețuri construcții. Întreabă-mă orice despre costuri de manoperă sau materiale!\n\nExemple:\n• Cât costă montajul rigips?\n• Care e prețul pentru hidroizolație?\n• Cât costă tencuiala mecanizată?"
    }
  ]);
  
  const [supportMessages, setSupportMessages] = useState([
    {
      role: "assistant",
      content: "Bună! 👋 Sunt aici să te ajut cu platforma e-Tesa.\n\nPoți să mă întrebi despre fluxurile principale:\n• 🧮 Generare **deviz** (modul DevizPRO + AI)\n• 📜 Creare **contract** legat de deviz\n• 🏗️ **Proiecte** cu faze și pipeline\n• 📎 **Anexe** la contracte/devize\n• 📋 **Procese verbale** (recepție, predare-primire)\n• 🧾 **Facturi** și e-Factura ANAF\n• 💰 Sistem **credite** (200 bonus la deschidere cont)\n• 📅 **Pontaj**, 👥 angajați, 🚗 foi parcurs\n\n🎤 Apasă microfonul ca să vorbești în loc să scrii!\n\nCum te pot ajuta?"
    }
  ]);
  
  const [errorMessages, setErrorMessages] = useState([
    {
      role: "assistant",
      content: "🚨 **Sesizare Eroare**\n\nDescrie problema întâmpinată cât mai detaliat:\n\n• Ce acțiune încercai să faci?\n• Ce mesaj de eroare ai primit?\n• În ce secțiune a apărut?\n\nEchipa tehnică va analiza și rezolva problema.\n\n📝 Scrie 'status erori' pentru a vedea erorile raportate."
    }
  ]);
  
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Voice-to-text: pornește/oprește înregistrarea și trimite audio la Whisper
  const toggleRecording = async () => {
    if (isRecording) {
      // Stop recording
      try {
        mediaRecorderRef.current?.stop();
      } catch (e) {
        console.error("Stop recording error:", e);
      }
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Microfonul nu este disponibil pe acest browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mr.onstop = async () => {
        // Eliberăm microfonul
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);

        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size === 0) return;

        setTranscribing(true);
        try {
          const formData = new FormData();
          formData.append("audio", blob, "voice.webm");
          const res = await axios.post(`${API}/voice/transcribe`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          const text = (res.data?.text || "").trim();
          if (text) {
            setInput((prev) => (prev ? prev + " " + text : text));
            inputRef.current?.focus();
          }
        } catch (err) {
          console.error("Transcribe error:", err);
          alert("Nu am putut transcrie audio. Încearcă din nou.");
        } finally {
          setTranscribing(false);
        }
      };

      mr.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone error:", err);
      alert("Nu am putut accesa microfonul. Verifică permisiunile.");
    }
  };

  const getMessages = () => {
    switch(chatMode) {
      case "prices": return pricesMessages;
      case "error": return errorMessages;
      default: return supportMessages;
    }
  };
  
  const setMessages = (updater) => {
    switch(chatMode) {
      case "prices": return setPricesMessages(updater);
      case "error": return setErrorMessages(updater);
      default: return setSupportMessages(updater);
    }
  };

  const messages = getMessages();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [pricesMessages, supportMessages, errorMessages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    const userData = getUserData();
    const companyData = {
      company_id: userData?.company_id || localStorage.getItem('company_id') || 'unknown',
      company_name: userData?.company_name || localStorage.getItem('company_name') || 'Necunoscut',
      user_email: userData?.email || 'unknown',
      user_name: userData?.name || 'Unknown'
    };

    if (chatMode === "error") {
      // Modul Eroare
      try {
        // Verifică dacă e comandă status
        if (userMessage.toLowerCase().includes("status erori")) {
          // Trimit company_id pentru filtrare - adminul vede doar erorile proprii
          const response = await axios.get(`${API}/support/errors`, {
            params: { company_id: companyData.company_id }
          });
          const errors = response.data.errors || [];
          
          if (errors.length === 0) {
            setMessages(prev => [...prev, { 
              role: "assistant", 
              content: "✅ Nu există erori raportate de firma ta momentan." 
            }]);
          } else {
            let errorList = "📋 **Erorile tale raportate:**\n\n";
            errors.forEach((err, idx) => {
              const date = new Date(err.created_at).toLocaleDateString('ro-RO');
              const status = err.resolved ? "✅ Rezolvat" : "⏳ În așteptare";
              errorList += `**${idx + 1}.** ${err.description.substring(0, 100)}${err.description.length > 100 ? '...' : ''}\n`;
              errorList += `   📅 ${date} | ${status}\n`;
              if (err.resolved && err.resolution_notes) {
                errorList += `   💬 Răspuns: ${err.resolution_notes}\n`;
              }
              errorList += '\n';
            });
            setMessages(prev => [...prev, { 
              role: "assistant", 
              content: errorList 
            }]);
          }
        } else {
          // Salvează eroarea
          await axios.post(`${API}/support/error`, {
            description: userMessage,
            timestamp: new Date().toISOString(),
            ...companyData
          });
          
          setMessages(prev => [...prev, { 
            role: "assistant", 
            content: "✅ **Sesizare Înregistrată!**\n\nVom analiza problema dumneavoastră și o vom remedia în cel mai scurt timp.\n\n📧 Veți fi contactat când eroarea va fi rezolvată.\n\nVă mulțumim pentru răbdare!" 
          }]);
        }
      } catch (error) {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "✅ **Sesizare Înregistrată!**\n\nVom analiza problema dumneavoastră și o vom remedia în cel mai scurt timp.\n\nVă mulțumim pentru răbdare!" 
        }]);
      } finally {
        setLoading(false);
      }
    } else if (chatMode === "support") {
      // Modul Suport
      try {
        const response = findSupportResponse(userMessage);
        const hasResponse = !!response;
        
        // Salvează întrebarea
        await axios.post(`${API}/support/question`, {
          question: userMessage,
          timestamp: new Date().toISOString(),
          needs_followup: !hasResponse,
          has_response: hasResponse,
          type: "support",
          ...companyData
        }).catch(() => {});

        if (response) {
          setMessages(prev => [...prev, { 
            role: "assistant", 
            content: response 
          }]);
        } else {
          setMessages(prev => [...prev, { 
            role: "assistant", 
            content: "📨 **Întrebare înregistrată!**\n\nNu am găsit un răspuns imediat pentru întrebarea ta. Am transmis-o echipei noastre de suport.\n\n✅ Un coleg te va contacta în curând cu un răspuns sau o soluție.\n\nÎntre timp, poți:\n• Consulta tutorialul (🎓)\n• Verifica secțiunea de Setări\n• Încerca o altă formulare a întrebării" 
          }]);
        }
      } catch (error) {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "Scuze, am întâmpinat o eroare. Te rog încearcă din nou." 
        }]);
      } finally {
        setLoading(false);
      }
    } else {
      // Modul Prețuri - API AI
      try {
        const response = await axios.post(`${API}/assistant/chat`, {
          message: userMessage,
          session_id: sessionId
        });

        setSessionId(response.data.session_id);
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: response.data.response 
        }]);
      } catch (error) {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "Scuze, am întâmpinat o eroare. Te rog încearcă din nou." 
        }]);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const switchMode = (mode) => {
    setChatMode(mode);
  };

  // Culori bazate pe mod
  const getGradientClass = () => {
    switch(chatMode) {
      case "prices": return "from-violet-600 to-indigo-600";
      case "error": return "from-red-500 to-rose-600";
      default: return "from-teal-500 to-cyan-500";
    }
  };
  
  const getHoverGradientClass = () => {
    switch(chatMode) {
      case "prices": return "hover:from-violet-700 hover:to-indigo-700";
      case "error": return "hover:from-red-600 hover:to-rose-700";
      default: return "hover:from-teal-600 hover:to-cyan-600";
    }
  };

  const getAccentColor = () => {
    switch(chatMode) {
      case "prices": return "violet";
      case "error": return "red";
      default: return "teal";
    }
  };

  const gradientClass = getGradientClass();
  const hoverGradientClass = getHoverGradientClass();
  const accentColor = getAccentColor();

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center text-white hover:scale-110 z-50"
        data-testid="ai-chat-button"
        title="Ajutor & Suport"
      >
        <Headphones className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full border-2 border-teal-500 flex items-center justify-center">
          <HelpCircle className="w-3 h-3 text-teal-600" />
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${
        isMinimized ? 'w-72 h-14' : 'w-96 h-[520px]'
      }`}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden h-full flex flex-col">
        {/* Header */}
        <div 
          className={`bg-gradient-to-r ${gradientClass} text-white p-3 flex items-center justify-between cursor-pointer transition-all duration-300`}
          onClick={() => isMinimized && setIsMinimized(false)}
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              {chatMode === "prices" ? <Sparkles className="w-4 h-4" /> : 
               chatMode === "error" ? <AlertTriangle className="w-4 h-4" /> :
               <Headphones className="w-4 h-4" />}
            </div>
            <div>
              <h3 className="font-semibold text-sm">
                {chatMode === "prices" ? "Asistent Prețuri" : 
                 chatMode === "error" ? "Sesizare Eroare" :
                 "Suport Platformă"}
              </h3>
              {!isMinimized && (
                <p className="text-xs text-white/70">
                  {chatMode === "prices" ? "Construcții & Materiale" : 
                   chatMode === "error" ? "Raportează o problemă" :
                   "Ajutor e-Tesa"}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
              className="h-7 w-7 p-0 text-white hover:bg-white/20"
            >
              <Minimize2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              className="h-7 w-7 p-0 text-white hover:bg-white/20"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Mode Selector - 3 tabs */}
            <div className="flex border-b border-slate-200">
              <button
                onClick={() => switchMode("support")}
                className={`flex-1 py-2 px-2 text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                  chatMode === "support" 
                    ? "bg-teal-50 text-teal-700 border-b-2 border-teal-500" 
                    : "text-slate-500 hover:bg-slate-50"
                }`}
                data-testid="chat-mode-support"
              >
                <Headphones className="w-3.5 h-3.5" />
                Suport
              </button>
              <button
                onClick={() => switchMode("prices")}
                className={`flex-1 py-2 px-2 text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                  chatMode === "prices" 
                    ? "bg-violet-50 text-violet-700 border-b-2 border-violet-500" 
                    : "text-slate-500 hover:bg-slate-50"
                }`}
                data-testid="chat-mode-prices"
              >
                <Calculator className="w-3.5 h-3.5" />
                Prețuri
              </button>
              <button
                onClick={() => switchMode("error")}
                className={`flex-1 py-2 px-2 text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                  chatMode === "error" 
                    ? "bg-red-50 text-red-700 border-b-2 border-red-500" 
                    : "text-slate-500 hover:bg-slate-50"
                }`}
                data-testid="chat-mode-error"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                Eroare
              </button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-3">
                {messages.map((msg, idx) => (
                  <div
                    key={msg.id || `msg-${msg.role}-${idx}`}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                        msg.role === "user"
                          ? `bg-gradient-to-br ${gradientClass} text-white rounded-br-md`
                          : "bg-slate-100 text-slate-800 rounded-bl-md"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {msg.role === "assistant" && (
                          <Bot className={`w-4 h-4 mt-1 flex-shrink-0 ${
                            chatMode === "error" ? "text-red-600" :
                            chatMode === "prices" ? "text-violet-600" : "text-teal-600"
                          }`} />
                        )}
                        <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                        {msg.role === "user" && (
                          <User className="w-4 h-4 mt-1 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Bot className={`w-4 h-4 ${
                          chatMode === "error" ? "text-red-600" :
                          chatMode === "prices" ? "text-violet-600" : "text-teal-600"
                        }`} />
                        <div className="flex gap-1">
                          <span className={`w-2 h-2 rounded-full animate-bounce ${
                            chatMode === "error" ? "bg-red-400" :
                            chatMode === "prices" ? "bg-violet-400" : "bg-teal-400"
                          }`} style={{ animationDelay: "0ms" }} />
                          <span className={`w-2 h-2 rounded-full animate-bounce ${
                            chatMode === "error" ? "bg-red-400" :
                            chatMode === "prices" ? "bg-violet-400" : "bg-teal-400"
                          }`} style={{ animationDelay: "150ms" }} />
                          <span className={`w-2 h-2 rounded-full animate-bounce ${
                            chatMode === "error" ? "bg-red-400" :
                            chatMode === "prices" ? "bg-violet-400" : "bg-teal-400"
                          }`} style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-3 border-t border-slate-100">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    transcribing ? "Se transcrie audio..." :
                    isRecording ? "🎤 Se înregistrează... apasă din nou pentru a opri" :
                    chatMode === "prices" ? "Întreabă despre prețuri..." : 
                    chatMode === "error" ? "Descrie eroarea..." :
                    "Cum te pot ajuta? (sau apasă 🎤)"
                  }
                  className={`flex-1 rounded-full bg-slate-50 border-slate-200 focus:ring-2 ${
                    chatMode === "error" ? "focus:ring-red-400 focus:border-red-400" :
                    chatMode === "prices" ? "focus:ring-violet-400 focus:border-violet-400" :
                    "focus:ring-teal-400 focus:border-teal-400"
                  }`}
                  disabled={loading || transcribing}
                  data-testid="ai-chat-input"
                />
                <Button
                  onClick={toggleRecording}
                  disabled={loading || transcribing}
                  className={`rounded-full w-10 h-10 p-0 ${isRecording
                    ? "bg-red-500 hover:bg-red-600 animate-pulse"
                    : "bg-slate-200 hover:bg-slate-300 text-slate-700"}`}
                  title={isRecording ? "Oprește înregistrarea" : "Înregistrează vocal"}
                  data-testid="ai-chat-mic"
                >
                  {transcribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isRecording ? (
                    <MicOff className="w-4 h-4 text-white" />
                  ) : (
                    <Mic className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className={`rounded-full w-10 h-10 p-0 bg-gradient-to-br ${gradientClass} ${hoverGradientClass}`}
                  data-testid="ai-chat-send"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 text-center mt-2">
                {chatMode === "prices" 
                  ? "Powered by GPT-5.2 • Prețuri orientative"
                  : chatMode === "error"
                  ? "🚨 Erorile sunt trimise echipei tehnice"
                  : "Suport e-Tesa • Întrebări salvate"
                }
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
